import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  MessageCircle,
  MessagesSquare,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Users,
  Video,
} from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminConversazioni,
  useCreateConversazione,
  useDeleteConversazione,
  useUpdateConversazione,
} from "@/hooks/useAdminConversazioni";
import { useMe } from "@/hooks/useMe";
import { ApiError } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { Interlocuzione, InterlocTipo } from "@/types/api";

const TIPI: { value: InterlocTipo; label: string; icon: typeof Phone }[] = [
  { value: "chiamata_voce", label: "Chiamata", icon: Phone },
  { value: "videocall", label: "Videocall", icon: Video },
  { value: "email", label: "Email", icon: Mail },
  { value: "incontro_presenza", label: "Incontro in presenza", icon: Users },
  { value: "messaggio_chat", label: "Chat", icon: MessageCircle },
  { value: "whatsapp", label: "WhatsApp", icon: MessagesSquare },
];

function tipoIcon(t: InterlocTipo) {
  return TIPI.find((x) => x.value === t)?.icon ?? MessageCircle;
}

function tipoLabel(t: InterlocTipo): string {
  return TIPI.find((x) => x.value === t)?.label ?? t;
}

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AdminConversazioniPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: interlocuzioni = [], isLoading } = useAdminConversazioni();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Interlocuzione | null>(null);

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Backoffice Maitime
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Conversazioni
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl pt-1">
            Registra chiamate, email, videocall, incontri. Quelle con visibilità cliente compaiono al cliente nella sezione Conversazioni.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          Registra
        </Button>
      </header>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : interlocuzioni.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-8 text-center text-sm text-muted-foreground">
          Nessuna interlocuzione registrata.
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-3 py-2 w-[140px]">Quando</th>
                <th className="text-left font-medium px-3 py-2 w-[140px]">Tipo</th>
                <th className="text-left font-medium px-3 py-2">Titolo / Sintesi</th>
                <th className="text-right font-medium px-3 py-2 w-[80px]">Durata</th>
                <th className="text-left font-medium px-3 py-2 w-[120px]">Visibilità</th>
                <th className="text-right font-medium px-3 py-2 w-[80px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {interlocuzioni.map((i) => (
                <InterlocRow key={i.id} item={i} onEdit={() => setEditing(i)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <FormDialog onClose={() => setCreating(false)} />}
      {editing && (
        <FormDialog item={editing} onClose={() => setEditing(null)} />
      )}
    </PageContainer>
  );
}

function InterlocRow({
  item: i,
  onEdit,
}: {
  item: Interlocuzione;
  onEdit: () => void;
}) {
  const elimina = useDeleteConversazione();
  const Icon = tipoIcon(i.tipo);

  function handleDelete() {
    if (!confirm(`Eliminare l'interlocuzione "${i.titolo}"?`)) return;
    elimina.mutate(i.id);
  }

  return (
    <tr className={cn(elimina.isPending && "opacity-50")}>
      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">
        {formatDataOra(i.data)}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs">
          <Icon className="size-3.5 text-muted-foreground" />
          {tipoLabel(i.tipo)}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-foreground">{i.titolo}</div>
        {i.sintesi && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {i.sintesi}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right text-xs text-muted-foreground tabular-nums">
        {i.durata_minuti != null ? `${i.durata_minuti} min` : "—"}
      </td>
      <td className="px-3 py-2">
        {i.is_visibile_cliente ? (
          <Badge
            variant="outline"
            className="text-[10px] bg-success/10 text-success border-success/30"
          >
            <Eye className="size-3 mr-1" /> cliente
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
            <EyeOff className="size-3 mr-1" /> solo Maitime
          </Badge>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={elimina.isPending}
          >
            {elimina.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 className="size-3.5 text-destructive" />
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function FormDialog({
  item,
  onClose,
}: {
  item?: Interlocuzione;
  onClose: () => void;
}) {
  const create = useCreateConversazione();
  const update = useUpdateConversazione();
  const mutation = item ? update : create;

  const [tipo, setTipo] = useState<InterlocTipo>(item?.tipo ?? "videocall");
  const [titolo, setTitolo] = useState(item?.titolo ?? "");
  // datetime-local prende `YYYY-MM-DDTHH:MM` senza secondi/timezone
  const [data, setData] = useState(
    item ? new Date(item.data).toISOString().slice(0, 16) : ""
  );
  const [durata, setDurata] = useState(item?.durata_minuti?.toString() ?? "");
  const [sintesi, setSintesi] = useState(item?.sintesi ?? "");
  const [noteInterne, setNoteInterne] = useState(item?.note_interne ?? "");
  const [visibile, setVisibile] = useState(item?.is_visibile_cliente ?? true);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const dataIso = new Date(data).toISOString();
    const durataN = durata.trim() ? parseInt(durata, 10) : null;
    const body = {
      tipo,
      titolo: titolo.trim(),
      data: dataIso,
      durata_minuti: durataN,
      sintesi: sintesi.trim() || null,
      note_interne: noteInterne.trim() || null,
      is_visibile_cliente: visibile,
    };

    if (item) {
      update.mutate({ id: item.id, body }, { onSuccess: () => onClose() });
    } else {
      create.mutate(body, { onSuccess: () => onClose() });
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {item ? "Modifica interlocuzione" : "Registra nuova interlocuzione"}
          </DialogTitle>
          <DialogDescription>
            Chiamate, email, videocall, incontri. Quelle visibili al cliente compaiono nella sua sezione Conversazioni.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as InterlocTipo)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {TIPI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Quando</label>
              <input
                type="datetime-local"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Titolo</label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              required
              maxLength={200}
              placeholder="Es: Call settimanale con Enzo e Fabio"
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Durata (min)</label>
              <input
                type="number"
                min="0"
                max="1440"
                value={durata}
                onChange={(e) => setDurata(e.target.value)}
                placeholder="opzionale"
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Visibilità</label>
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibile}
                  onChange={(e) => setVisibile(e.target.checked)}
                  className="size-4"
                />
                <span className="text-sm">visibile al cliente</span>
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Sintesi (vista cliente)</label>
            <Textarea
              value={sintesi}
              onChange={(e) => setSintesi(e.target.value)}
              rows={3}
              placeholder="Cosa abbiamo discusso, decisioni prese."
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Note interne (solo Maitime)</label>
            <Textarea
              value={noteInterne}
              onChange={(e) => setNoteInterne(e.target.value)}
              rows={2}
              placeholder="Annotazioni private, follow-up, ecc."
            />
          </div>

          {mutation.error instanceof ApiError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {mutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={mutation.isPending || !titolo.trim() || !data}>
              {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {item ? "Salva" : "Registra"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
